// umlaut-check: skip — "LkwLadung"/"verteilePaletten" sind Komponenten-
// bzw. Funktionsnamen (technische Bezeichner), bleiben ASCII.

import React, {useMemo} from "react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import Farbfeld from "./Farbfeld.jsx";
import {formatFaktor, formatMenge, formatProzent, useIstSchmal} from "./format.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

/**
 * Verteilt fraktionale Palettenzahlen je Eintrag auf ganze Stellplätze
 * (Größte-Rest-Verfahren): erst abrunden, dann die Einträge mit dem
 * größten Rest der Reihe nach um einen Stellplatz aufrunden, bis die
 * gerundete Gesamtsumme erreicht ist. So ergeben mehrere fraktionale
 * Palettenzahlen (z. B. 3,5 + 2,2 + 1,4 Paletten) in Summe immer genau so
 * viele Kästchen, wie insgesamt angezeigt werden sollen - keine
 * Rundungsdifferenz zwischen einzelnen Feldern und der Kopfzahl.
 * Einträge ohne positive Palettenzahl werden nicht gezeichnet.
 */
export function verteilePaletten(eintraege) {
    const positive = (eintraege || []).filter((e) => (Number(e.paletten) || 0) > 0);
    const zielSumme = Math.round(positive.reduce((summe, e) => summe + Number(e.paletten), 0));

    const basis = positive.map((e) => ({
        ...e,
        slots: Math.floor(Number(e.paletten)),
        rest: Number(e.paletten) - Math.floor(Number(e.paletten)),
    }));
    let vergeben = basis.reduce((summe, e) => summe + e.slots, 0);

    [...basis]
        .sort((a, b) => b.rest - a.rest)
        .some((eintrag) => {
            if (vergeben >= zielSumme) {
                return true;
            }
            eintrag.slots += 1;
            vergeben += 1;
            return false;
        });

    return basis;
}

// Kantenlänge eines Palettenfeldes in Pixeln. Bewusst klein gehalten: Das
// Raster soll auf einen Blick erfassbar sein und nicht die halbe Seite
// füllen - dieselbe Anmutung wie das Schaubild auf der Erklärseite.
const FELD_KANTE = 18;
const FELD_ABSTAND = 2;

function PaletteFeld({eintrag, slotKey}) {
    const inhalt = (
        <div className="text-start">
            <div className="d-flex align-items-center gap-1 fw-semibold">
                {eintrag.farbwert !== undefined && <Farbfeld farbwert={eintrag.farbwert} size={12} />}
                <span>{eintrag.label}</span>
            </div>
            {eintrag.unterlabel && <div className="small">{eintrag.unterlabel}</div>}
            <div className="small">
                {formatMenge(eintrag.menge)} Dosen · {eintrag.gruppeLabel}
            </div>
            {/* Der Tooltip hat einen dunklen Grund - die Bootstrap-Klasse für
                gedämpften Text wäre dort dunkelgrau auf dunkelgrau und praktisch
                unlesbar. Deshalb nur leicht abgesetzt über die Deckkraft. */}
            {eintrag.bezug && <div className="small" style={{opacity: .8}}>{eintrag.bezug}</div>}
        </div>
    );

    return (
        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`lkw-palette-${slotKey}`}>{inhalt}</Tooltip>}>
            <button
                type="button"
                className="p-0 border-0"
                style={{width: FELD_KANTE, height: FELD_KANTE, backgroundColor: eintrag.farbe, borderRadius: 2, cursor: "help"}}
                aria-label={`Palette: ${eintrag.label}, ${formatMenge(eintrag.menge)} Dosen, ${eintrag.gruppeLabel}${eintrag.bezug ? `, ${eintrag.bezug}` : ""}`}
            />
        </OverlayTrigger>
    );
}

function FreiesFeld({slotKey, label}) {
    return (
        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`lkw-palette-frei-${slotKey}`}>{label}</Tooltip>}>
            <button
                type="button"
                className="p-0"
                style={{width: FELD_KANTE, height: FELD_KANTE, backgroundColor: "#fff", border: "1px dashed #ced4da", borderRadius: 2, cursor: "help"}}
                aria-label={label}
            />
        </OverlayTrigger>
    );
}

/**
 * Interaktive Lastwagen-Darstellung (Auftrag 22.09.2026, Wortlaut Thorsten:
 * "bei Mouseover auch Details zu jeder Palette darstellen"): alle
 * Palettenstellplätze eines LKW als Raster, Führerhaus links wie im
 * Vorbild resources/js/Components/Cosmos/Erklaerung/LkwSchaubild.jsx
 * (davon nur das Muster übernommen, die Datei selbst bleibt unangetastet -
 * sie gehört der Erklärseite). Jedes belegte Feld ist eine Palette mit
 * Tooltip (Farbton, Menge, Marke, Bezug); freie Felder sind als solche
 * erkennbar und lösen keine Warnung aus - ein nicht ganz voller LKW ist
 * laut Fachauskunft kein Fehlerzustand, Cosmos liefert im Notfall auch
 * Teilladungen (nur weniger rentabel). Einzig eine ÜBERLADUNG (mehr
 * Paletten als Kapazität) wird hervorgehoben, wie an den bestehenden
 * LKW-Balken (LkwBalken.jsx) auch.
 *
 * "eintraege": Array von {id, label, unterlabel?, menge, paletten, farbwert?,
 * gruppe, gruppeLabel, farbe, bezug?} - eine Palette kann in Wirklichkeit
 * mehrere solcher Zeilen zusammenfassen (fraktionale Palettenzahl je
 * Zeile), verteilePaletten() rundet auf ganze Stellplätze.
 */
export default function LkwLadung({kapazitaetPaletten, eintraege, leerLabel = "Freier Stellplatz – noch nicht belegt.", zusammenfassung = true}) {
    const schmal = useIstSchmal(576);
    const spalten = schmal ? 8 : 16;

    const {slots, gesamtSlots, belegteSlots, ueberladen} = useMemo(() => {
        const kap = Math.max(0, Math.round(Number(kapazitaetPaletten) || 0));
        const verteilt = verteilePaletten(eintraege);
        const belegt = verteilt.reduce((summe, e) => summe + e.slots, 0);
        const gesamt = Math.max(kap, belegt);

        const flach = [];
        verteilt.forEach((eintrag) => {
            for (let i = 0; i < eintrag.slots; i += 1) {
                flach.push(eintrag);
            }
        });

        return {slots: flach, gesamtSlots: gesamt, belegteSlots: belegt, ueberladen: kap > 0 && belegt > kap};
    }, [eintraege, kapazitaetPaletten]);

    const freieSlots = Math.max(0, gesamtSlots - belegteSlots);
    const prozent = gesamtSlots > 0 ? (belegteSlots / gesamtSlots) * 100 : 0;

    const gruppen = useMemo(() => {
        const karte = new Map();
        for (const eintrag of eintraege || []) {
            if (!(Number(eintrag.paletten) > 0)) {
                continue;
            }
            const schluessel = eintrag.gruppe || "unbekannt";
            const bisher = karte.get(schluessel) || {gruppe: schluessel, label: eintrag.gruppeLabel, farbe: eintrag.farbe, menge: 0, paletten: 0};
            bisher.menge += Number(eintrag.menge) || 0;
            bisher.paletten += Number(eintrag.paletten) || 0;
            karte.set(schluessel, bisher);
        }
        return Array.from(karte.values());
    }, [eintraege]);

    if (gesamtSlots === 0) {
        return <p className="text-muted small mb-0">Keine Palettenkapazität hinterlegt.</p>;
    }

    return (
        <div>
            {/* Kompakt wie das Schaubild der Erklärseite (Thorsten: „so wie du es
                zum Beispiel schon in der Präsentation gemacht hast"): Die
                Ladefläche wächst NICHT auf die volle Seitenbreite, sondern die
                Felder behalten eine feste, kleine Kantenlänge. Sonst wird das
                Raster bei 16 Spalten über einen halben Bildschirm hoch und
                erschlägt die Zeilen darunter. */}
            <div className="d-flex align-items-stretch gap-2" style={{maxWidth: spalten * (FELD_KANTE + FELD_ABSTAND) + 30}}>
                <div className="flex-shrink-0 rounded-1" style={{width: 12, backgroundColor: "#495057"}} aria-hidden="true" />
                <div
                    className={`flex-grow-1 rounded-1 p-1 ${ueberladen ? "border border-danger border-2" : "border"}`}
                    style={{backgroundColor: "#f8f9fa", display: "grid", gridTemplateColumns: `repeat(${spalten}, ${FELD_KANTE}px)`, gap: FELD_ABSTAND, justifyContent: "start"}}
                    role="group"
                    aria-label={`Ladefläche: ${belegteSlots} von ${gesamtSlots} Palettenstellplätzen belegt${ueberladen ? ", überladen" : ""}.`}
                >
                    {slots.map((eintrag, i) => (
                        <PaletteFeld key={`${eintrag.id}-${i}`} eintrag={eintrag} slotKey={`${eintrag.id}-${i}`} />
                    ))}
                    {Array.from({length: freieSlots}).map((_, i) => (
                        <FreiesFeld key={`frei-${i}`} slotKey={i} label={leerLabel} />
                    ))}
                </div>
            </div>

            {zusammenfassung && (
                <div className={`small mt-1 ${ueberladen ? "text-danger fw-semibold" : "text-muted"}`}>
                    {belegteSlots} von {gesamtSlots} Palettenstellplätzen belegt ({formatProzent(prozent, 0)})
                    {ueberladen && " – mehr Paletten als Kapazität, das ist mehr als ein LKW."}
                </div>
            )}

            {gruppen.length > 0 && (
                <div className="d-flex flex-wrap gap-3 mt-1 small">
                    {gruppen.map((g) => (
                        <span key={g.gruppe} className="d-flex align-items-center gap-1">
                            <span className="rounded-1 flex-shrink-0" style={{width: 12, height: 12, backgroundColor: g.farbe}} aria-hidden="true" />
                            <span>
                                {g.label}: {formatMenge(g.menge)} Dosen ({formatFaktor(g.paletten, 1)} Pal.)
                            </span>
                        </span>
                    ))}
                    {freieSlots > 0 && (
                        <span className="d-flex align-items-center gap-1 text-muted">
                            <span className="rounded-1 flex-shrink-0 border" style={{width: 12, height: 12, backgroundColor: "#fff"}} aria-hidden="true" />
                            <span>Frei: {freieSlots} Stellplätze</span>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
